import React from 'react';
import { createPortal } from 'react-dom';
import { Popup, Icon } from 'semantic-ui-react';
import './ProcessGuide.css';

/**
 * ProcessGuide — the order the pipeline's steps have to happen in, on hover.
 *
 * WHY THIS EXISTS. Several steps are only free, or only possible, if they come before another
 * one. Merging characters is the sharp case: auto-cast writes a VoiceClone, the merge guard
 * reads that as recorded work, and merging is blocked from then on. Nothing in the UI says so,
 * and the failure is silent — the buttons are all still there, they just stop working the way
 * you expect. The same is true of deriving before casting, and of setting the ACX metadata
 * before the first render rather than after it.
 *
 * DELIBERATELY A HINT, NOT A WIZARD. It does not track progress, gate anything, or know what
 * you have already done. It is the running order written down where the work happens, which is
 * the smallest thing that answers "what was I supposed to do first?".
 *
 * PER PAGE. A page with no sequence renders nothing at all rather than an empty popup — the icon
 * appearing is itself the signal that there is an order worth knowing here.
 *
 * THE PAGE ASKS FOR ITS GUIDE; THE URL IS NOT CONSULTED. It used to prefix-match the pathname
 * from a single mount in Layout, and that silently showed nothing for the two pages it was
 * written for. Both are reachable two ways — by their own route AND as a dashboard sub-page
 * (DashBoardPages switches on a subPageId with the URL still reading /dashBoard) — and the
 * dashboard route is the way in that people actually use. Matching a URL that the page does not
 * control meant the guide was absent exactly where it was needed.
 *
 * Mount it in the page instead, with the key of the guide it wants.
 *
 * AND PORTAL IT TO document.body. Being mounted in the page is what makes the key correct, and it
 * is also what buries it: `.main-content` sets `isolation: isolate` — deliberately, so page
 * content can never paint over the sticky navbar — which means a fixed element inside it resolves
 * its z-index INSIDE that context, below the navbar's opaque white z-index:1000 band. The button
 * was in the DOM, at top:20px, behind the navbar. The portal puts it back in the body stacking
 * context, where it competes with the navbar as a peer, exactly as it did when Layout mounted it.
 */

// Guide key → the sequence it describes. The KEY is the contract, chosen by the page.
const GUIDES = [
  {
    id: 'litgroup-builder',
    title: 'Create a Literary Group and import its books',
    steps: [
      ['Create the Literary Group', 'Name, genre, author — and its profile picture, which can be set now rather than after publishing.'],
      ['Add one or more books', 'A PDF and a cover each. Books can be added to the group later too.'],
      ['Review, then Mark as Ready', 'Ready is the ONLY status publish accepts — it will refuse a draft. Reversible: "revert to draft" sits beside it.'],
      ['Publish this Literary Group', 'Creates the group and imports every book: text, characters, front matter, credits. Runs in the background; you can leave the page.'],
      ['Derive casting', 'Assigns archetypes and tiers. Auto-cast finds NO targets without it.'],
      ['Merge duplicate characters', 'Free ONLY before casting — casting writes a voice clone that blocks merging.'],
      ['Assign any speaker-less dialogue lines', 'Lines with no speaker do not render at all.'],
      ['Auto-cast', 'Watch the "reused" count — reused voices cost no ElevenLabs operations.'],
      ['Audiobook credits (book Edit panel)', 'Cast credit, rights holder, copyright year, square cover.'],
    ],
    // The badge on every card in the left-hand list, and nowhere in the UI does it say what any
    // of them mean. "Ready" is the one that actually catches people: it looks like a finished
    // state and is in fact a waiting one.
    legend: {
      title: 'What the status badge on each card means',
      items: [
        ['Draft', 'Being built. Editable, and nothing exists yet beyond this record.'],
        ['Ready', 'Marked finished and WAITING — nothing happens until you press publish. A group can sit here for weeks; being ready is not being published.'],
        ['Processing', 'The import is running. Cannot be edited or deleted until it finishes.'],
        ['Complete', 'Published. A live Literary Group exists, and its books read their audiobook credits from this record — which is why deleting it is refused while that group is alive.'],
        ['Archived', 'Soft-deleted under the old behaviour. Still present, and still holding whatever points at it. Delete now removes for real.'],
      ],
    },
  },
  {
    id: 'rendering-studio',
    title: 'Render a book',
    steps: [
      ['Check coverage', 'Unvoiced characters fall back to the Narrator. Speaker-less lines do not render.'],
      ['Regenerate credits after any metadata change', 'Otherwise the book still speaks the old credit.'],
      ['Render book.full', 'Opening credits, front matter, body, closing credits — in one unit.'],
      ['Preflight, then stitch', 'Stitch writes one file per chapter, plus chapter 0 and the closing credits.'],
      ['Master, then export', 'ACX targets are checked in the Audio Lab.'],
    ],
  },
];

const ProcessGuide = ({ guideKey }) => {
  const guide = GUIDES.find((g) => g.id === guideKey);
  if (!guide) return null;

  return createPortal((
    <div className="process-guide">
      <Popup
        on="hover"
        position="bottom right"
        hoverable
        wide="very"
        trigger={(
          <button type="button" className="process-guide-btn" aria-label={`${guide.title} — step order`}>
            <Icon name="list ol" />
          </button>
        )}
      >
        <Popup.Header>{guide.title} — the order that matters</Popup.Header>
        <Popup.Content>
          <ol className="process-guide-steps">
            {guide.steps.map(([step, why]) => (
              <li key={step}>
                <strong>{step}</strong>
                <span>{why}</span>
              </li>
            ))}
          </ol>
          {/* Vocabulary, not sequence — so it sits below the numbered list rather than inside it.
              Knowing what a status means is a different question from knowing what to do next,
              and numbering it would imply it was a step. */}
          {guide.legend && (
            <div className="process-guide-legend">
              <div className="process-guide-legend-title">{guide.legend.title}</div>
              <dl>
                {guide.legend.items.map(([term, meaning]) => (
                  <React.Fragment key={term}>
                    <dt>{term}</dt>
                    <dd>{meaning}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          )}
        </Popup.Content>
      </Popup>
    </div>
  ), document.body);
};

export default ProcessGuide;
